import { CheckCircle2, Sparkles, Trophy } from 'lucide-react';
import { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CongratulationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CongratulationsDialog = ({
  open,
  onOpenChange,
}: CongratulationsDialogProps) => {
  const { width, height } = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (open) {
      setShowConfetti(true);
      // Give enough time for all confetti to fall naturally
      // With gravity of 0.1 and typical screen heights, 15 seconds should be enough
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 15000);

      return () => clearTimeout(timer);
    } else {
      // Clean up confetti when dialog closes
      setShowConfetti(false);
    }
  }, [open]);

  const handleContinueToDashboard = () => {
    onOpenChange(false);
  };

  return (
    <>
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={200}
          gravity={0.1}
          initialVelocityX={5}
          initialVelocityY={10}
          style={{ zIndex: 9999 }}
        />
      )}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="sr-only">Congratulations</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center space-y-6 py-8">
            <div className="relative">
              <div className="bg-green-400/20 absolute -inset-4 animate-pulse rounded-full" />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-green-500">
                <Trophy className="h-10 w-10 text-white" />
              </div>
            </div>

            <div className="space-y-2 text-center">
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="h-5 w-5 text-green-600" />
                <h2 className="as-h4 as-strong text-zinc-900">
                  Congratulations!
                </h2>
                <Sparkles className="h-5 w-5 text-green-600" />
              </div>
              <p className="as-body-03 text-zinc-600">
                You've successfully completed all stages of your discovery
                journey!
              </p>
            </div>

            <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span className="as-title-03 as-strong text-green-800">
                All stages completed
              </span>
            </div>

            <Button onClick={handleContinueToDashboard} className="w-full">
              Continue to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
